import { USER_SECURITY_INFO_KEY } from 'services/authorization.service';
import { AppClaimType, PolicyType, UserSecurityInfoModel, UserStructuralUnitModel } from 'types/user.interface';

export const IDENTITY_NUMBER_FIRST_PART_LENGTH = 6;
export const ADMIN_ROLE_NAME = 'LNA sistēmas administrators';
export const READING_ROOM_SERVICE_PROVIDER_ROLE = 'Pakalpojumu sniedzējs lasītavās';
export const READING_ROOM_SERVICE_LEADER_ROLE = 'Pakalpojumu vadītājs lasītavās';

export function formatIdentityNumber(identityNumber: string = ''): string {
  if (!identityNumber.includes('-') && identityNumber.length > IDENTITY_NUMBER_FIRST_PART_LENGTH) {
    const firstPart = identityNumber.slice(0, IDENTITY_NUMBER_FIRST_PART_LENGTH);
    const secondPart = identityNumber.slice(IDENTITY_NUMBER_FIRST_PART_LENGTH);

    return `${firstPart}-${secondPart}`;
  }

  return identityNumber;
}

export function checkClaims(policyType: PolicyType, claim: AppClaimType): boolean {
  const userSecurityInfo = JSON.parse(localStorage.getItem(USER_SECURITY_INFO_KEY) || '{}');

  if (userSecurityInfo) {
    const policy = userSecurityInfo?.policies.find((policy: any) => policy.type === policyType);

    if (policy) {
      // TODO: if BE changes
      return policy.claims.some((c: any) => c.claim === claim); //V2 c is string
    }
  }

  return false;
}

/**
 * Returns the user's structural-unit memberships in which they hold the given claim under the
 * given policy. A globally-scoped claim yields all memberships; otherwise the memberships are
 * intersected with the claim's scoped structural unit ids. Used to offer only the units relevant
 * to a capability (e.g. InformationRequest/Create) when selecting a responsible structural unit.
 */
export function getStructuralUnitsForClaim(
  securityInfo: UserSecurityInfoModel | null | undefined,
  policyType: PolicyType,
  claim: AppClaimType
): UserStructuralUnitModel[] {
  const allUnits = securityInfo?.structuralUnits ?? [];
  const matchingClaims =
    (securityInfo?.policies ?? []).find((policy) => policy.type === policyType)?.claims?.filter((c) => c.claim === claim) ?? [];
  if (matchingClaims.length === 0) {
    return [];
  }
  const scopedIds = new Set(matchingClaims.flatMap((c) => c.structuralUnits ?? []));
  // The explicit unit list is authoritative even when the claim is also flagged global; only a
  // global claim that enumerates no units at all is treated as applying to every membership.
  if (scopedIds.size === 0 && matchingClaims.some((c) => c.isGlobalScope)) {
    return [...allUnits];
  }
  return allUnits.filter((unit) => scopedIds.has(unit.id));
}

/**
 * Returns the structural unit ids scoped to a given policy (the distinct union of all its
 * claims' structuralUnits), plus whether any of that policy's claims is globally scoped. A
 * globally-scoped policy yields an empty id list on purpose — callers should treat an empty
 * list + isGlobalScope=true as "no restriction" rather than "no access", since sending an
 * empty structuralUnitIds filter to the API already means "unfiltered".
 */
export function getPolicyStructuralUnitIds(
  securityInfo: UserSecurityInfoModel | null | undefined,
  policyType: PolicyType
): { structuralUnitIds: string[]; isGlobalScope: boolean } {
  const claims = (securityInfo?.policies ?? []).find((policy) => policy.type === policyType)?.claims ?? [];
  const isGlobalScope = claims.some((c) => c.isGlobalScope);
  const structuralUnitIds = isGlobalScope ? [] : [...new Set(claims.flatMap((c) => c.structuralUnits ?? []))];

  return { structuralUnitIds, isGlobalScope };
}

export function isAdminUser(): boolean {
  const userSecurityInfo = JSON.parse(localStorage.getItem(USER_SECURITY_INFO_KEY) || '{}');

  if (userSecurityInfo) {
    return userSecurityInfo.roleNames.includes(ADMIN_ROLE_NAME);
  }

  return false;
}

export function allowEditReadingRoom(roomStructuralUnitId: string): boolean {
  const userSecurityInfo = JSON.parse(localStorage.getItem(USER_SECURITY_INFO_KEY) || '{}');
  const roles = userSecurityInfo.roleNames;

  if (roles.length === 3 && roles.includes(READING_ROOM_SERVICE_LEADER_ROLE) && roles.includes(READING_ROOM_SERVICE_PROVIDER_ROLE)) {
    return userSecurityInfo.structuralUnits.some((unit: any) => unit.id === roomStructuralUnitId);
  } else if (
    roles.length === 2 &&
    (roles.includes(READING_ROOM_SERVICE_LEADER_ROLE) || roles.includes(READING_ROOM_SERVICE_PROVIDER_ROLE))
  ) {
    return userSecurityInfo.structuralUnits.some((unit: any) => unit.id === roomStructuralUnitId);
  } else {
    return true;
  }
}

export function sortByCodeOrder<T extends { code?: string | null; name?: string | null }>(
  values: ReadonlyArray<T> | null | undefined,
  order: readonly string[]
): T[] {
  if (!values || values.length === 0) return [];
  const rank = new Map(order.map((c, i) => [c, i]));

  return [...values].sort((a, b) => {
    const ra = rank.get(a.code ?? '') ?? Number.POSITIVE_INFINITY;
    const rb = rank.get(b.code ?? '') ?? Number.POSITIVE_INFINITY;
    if (ra !== rb) return ra - rb;
    return (a.name ?? '').localeCompare(b.name ?? '');
  });
}
